import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { format, subDays, startOfMonth } from "date-fns";
import type { DateRange } from "react-day-picker";
import { toast } from "sonner";
import {
  useGetMe,
  useAdminGetEmailVolume,
  getAdminGetEmailVolumeQueryKey,
  useAdminGetEmailVolumeForUser,
  adminGetEmailVolumeCsv,
  type EmailVolumeAgentRow,
  type EmailVolumeReport,
} from "@workspace/api-client-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  BarChart3,
  CalendarIcon,
  Loader2,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

type Preset = "today" | "yesterday" | "7d" | "30d" | "thisMonth" | "custom";

function presetToRange(preset: Preset): DateRange | undefined {
  const today = new Date();
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const y = subDays(today, 1);
      return { from: y, to: y };
    }
    case "7d":
      return { from: subDays(today, 6), to: today };
    case "30d":
      return { from: subDays(today, 29), to: today };
    case "thisMonth":
      return { from: startOfMonth(today), to: today };
    case "custom":
      return undefined;
  }
}

function fmtDate(d: Date | undefined): string | undefined {
  return d ? format(d, "yyyy-MM-dd") : undefined;
}

function rangeLabel(r: DateRange | undefined): string {
  if (!r?.from) return "Pick a range";
  const f = format(r.from, "MMM d, yyyy");
  if (!r.to || r.to.getTime() === r.from.getTime()) return f;
  return `${f} – ${format(r.to, "MMM d, yyyy")}`;
}

type SortKey = "agent" | "total" | "queued" | string;

export default function AdminEmailAnalytics() {
  const [, navigate] = useLocation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  // Mirror the client-side admin guard used by other /app/admin/*
  // pages so non-admins land on /app instead of seeing the generic
  // 403 error UI from the API.
  useEffect(() => {
    if (meQuery.isLoading) return;
    if (!me) return;
    if (!me.isAdmin) navigate("/app");
  }, [me, meQuery.isLoading, navigate]);

  const [preset, setPreset] = useState<Preset>("today");
  const [range, setRange] = useState<DateRange | undefined>(presetToRange("today"));
  const [includeTest, setIncludeTest] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [drillUserId, setDrillUserId] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const csvAbortRef = useRef<AbortController | null>(null);

  // Pass the viewer's IANA timezone so the backend bucketed-by-day window
  // ("Today", "Yesterday", custom ranges) lines up with the local calendar
  // the user sees on screen — otherwise sends made just past local midnight
  // get bucketed into the previous UTC day and "Today" appears empty.
  const viewerTz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );
  const params = useMemo(
    () => ({
      ...(range?.from ? { from: fmtDate(range.from)! } : {}),
      ...(range?.to ? { to: fmtDate(range.to)! } : range?.from ? { to: fmtDate(range.from)! } : {}),
      includeTest,
      tz: viewerTz,
    }),
    [range, includeTest, viewerTz],
  );

  const { data, isLoading, isFetching, error } = useAdminGetEmailVolume(params, {
    query: {
      queryKey: getAdminGetEmailVolumeQueryKey(params),
      enabled: me?.isAdmin === true,
    },
  });

  const onPresetChange = (next: Preset) => {
    setPreset(next);
    if (next !== "custom") setRange(presetToRange(next));
  };

  const onRangeChange = (next: DateRange | undefined) => {
    setPreset("custom");
    setRange(next);
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "agent" ? "asc" : "desc");
    }
  };

  const sortedRows = useMemo(() => {
    const rows = [...(data?.rows ?? [])];
    const dir = sortDir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      if (sortKey === "agent") {
        av = (a.name ?? a.email ?? "").toLowerCase();
        bv = (b.name ?? b.email ?? "").toLowerCase();
      } else if (sortKey === "total") {
        av = a.total;
        bv = b.total;
      } else if (sortKey === "queued") {
        av = a.queued;
        bv = b.queued;
      } else {
        // template column key, e.g. "tpl:42" or "tpl:none"
        av = templateCount(a, sortKey);
        bv = templateCount(b, sortKey);
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const totals = useMemo(() => {
    const t = { total: 0, queued: 0, perTemplate: new Map<string, number>() };
    for (const r of data?.rows ?? []) {
      t.total += r.total;
      t.queued += r.queued;
      for (const c of r.perTemplate) {
        const k = templateColKey(c.templateId);
        t.perTemplate.set(k, (t.perTemplate.get(k) ?? 0) + c.count);
      }
    }
    return t;
  }, [data]);

  const handleDownloadCsv = useCallback(async () => {
    if (csvAbortRef.current) csvAbortRef.current.abort();
    const controller = new AbortController();
    csvAbortRef.current = controller;
    setIsExporting(true);
    try {
      const csv = await adminGetEmailVolumeCsv(params, {
        signal: controller.signal,
      });
      const blob = new Blob([csv ?? ""], { type: "text/csv;charset=utf-8" });
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `email-volume-${params.from ?? "today"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
      toast.success("CSV downloaded");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      toast.error("CSV export failed");
    } finally {
      if (csvAbortRef.current === controller) {
        csvAbortRef.current = null;
        setIsExporting(false);
      }
    }
  }, [params]);

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary" />
              Email Analytics
            </h1>
            <p className="text-muted-foreground">
              Per-agent send volume, broken out by template. Failed sends are
              excluded; queued sends are counted as in-flight.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-[160px]">
              <Select value={preset} onValueChange={(v) => onPresetChange(v as Preset)}>
                <SelectTrigger data-testid="email-analytics-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="thisMonth">This month</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="email-analytics-date-trigger"
                >
                  <CalendarIcon className="w-4 h-4 mr-1.5" />
                  {rangeLabel(range)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={range}
                  onSelect={onRangeChange}
                  numberOfMonths={2}
                  disabled={{ after: new Date() }}
                  defaultMonth={range?.from ?? new Date()}
                />
              </PopoverContent>
            </Popover>
            <div className="flex items-center gap-2">
              <Switch
                id="include-test"
                checked={includeTest}
                onCheckedChange={setIncludeTest}
                data-testid="email-analytics-include-test"
              />
              <Label htmlFor="include-test" className="text-sm cursor-pointer select-none">
                Show test sends
              </Label>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCsv}
              disabled={isExporting}
              data-testid="email-analytics-download-csv"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1.5" />
              )}
              {isExporting ? "Exporting…" : "Download CSV"}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="py-12 text-center">
              <p className="text-destructive font-medium">
                Couldn't load email volume.
              </p>
              <p className="text-muted-foreground text-sm mt-2">
                Try refreshing the page.
              </p>
            </CardContent>
          </Card>
        ) : data ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Per-agent breakdown
                {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ReportTable
                report={data}
                rows={sortedRows}
                totals={totals}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                onDrill={setDrillUserId}
              />
            </CardContent>
          </Card>
        ) : null}
      </div>

      <Sheet open={drillUserId !== null} onOpenChange={(o) => !o && setDrillUserId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {drillUserId !== null && (
            <DrillDownPanel
              userId={drillUserId}
              from={params.from}
              to={params.to}
              includeTest={includeTest}
              tz={viewerTz}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function templateColKey(id: number | null): string {
  return id === null ? "tpl:none" : `tpl:${id}`;
}

function templateCount(row: EmailVolumeAgentRow, key: string): number {
  const targetId = key === "tpl:none" ? null : Number.parseInt(key.slice(4), 10);
  const cell = row.perTemplate.find((c) => c.templateId === targetId);
  return cell?.count ?? 0;
}

function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-50 inline ml-1" />;
  return dir === "asc" ? (
    <ArrowUp className="w-3 h-3 inline ml-1" />
  ) : (
    <ArrowDown className="w-3 h-3 inline ml-1" />
  );
}

function ReportTable({
  report,
  rows,
  totals,
  sortKey,
  sortDir,
  onSort,
  onDrill,
}: {
  report: EmailVolumeReport;
  rows: EmailVolumeAgentRow[];
  totals: { total: number; queued: number; perTemplate: Map<string, number> };
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  onSort: (key: SortKey) => void;
  onDrill: (userId: number) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground" data-testid="email-analytics-empty">
        No agents to show for this window.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <Table data-testid="email-analytics-table">
        <TableHeader>
          <TableRow>
            <TableHead>
              <button
                type="button"
                onClick={() => onSort("agent")}
                className="font-medium hover:text-foreground"
              >
                Agent <SortIcon active={sortKey === "agent"} dir={sortDir} />
              </button>
            </TableHead>
            {report.templates.map((t) => {
              const key = templateColKey(t.id);
              return (
                <TableHead key={key} className="text-right">
                  <button
                    type="button"
                    onClick={() => onSort(key)}
                    className="font-medium hover:text-foreground"
                    title={t.name}
                  >
                    <span className="max-w-[140px] truncate inline-block align-bottom">
                      {t.name}
                    </span>
                    <SortIcon active={sortKey === key} dir={sortDir} />
                  </button>
                </TableHead>
              );
            })}
            <TableHead className="text-right">
              <button type="button" onClick={() => onSort("queued")} className="font-medium hover:text-foreground">
                Queued <SortIcon active={sortKey === "queued"} dir={sortDir} />
              </button>
            </TableHead>
            <TableHead className="text-right">
              <button type="button" onClick={() => onSort("total")} className="font-medium hover:text-foreground">
                Total <SortIcon active={sortKey === "total"} dir={sortDir} />
              </button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.userId}
              className="cursor-pointer"
              onClick={() => onDrill(row.userId)}
              data-testid={`email-analytics-row-${row.userId}`}
            >
              <TableCell>
                <div className="font-medium">{row.name ?? row.email ?? `User #${row.userId}`}</div>
                {row.name && row.email && (
                  <div className="text-xs text-muted-foreground">{row.email}</div>
                )}
              </TableCell>
              {report.templates.map((t) => {
                const k = templateColKey(t.id);
                const v = templateCount(row, k);
                return (
                  <TableCell key={k} className="text-right tabular-nums">
                    {v > 0 ? v : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                );
              })}
              <TableCell className="text-right tabular-nums">
                {row.queued > 0 ? row.queued : <span className="text-muted-foreground">—</span>}
              </TableCell>
              <TableCell className="text-right tabular-nums font-medium">{row.total}</TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-muted/40 font-semibold" data-testid="email-analytics-totals-row">
            <TableCell>Total</TableCell>
            {report.templates.map((t) => {
              const k = templateColKey(t.id);
              return (
                <TableCell key={k} className="text-right tabular-nums">
                  {totals.perTemplate.get(k) ?? 0}
                </TableCell>
              );
            })}
            <TableCell className="text-right tabular-nums">{totals.queued}</TableCell>
            <TableCell className="text-right tabular-nums">{totals.total}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function DrillDownPanel({
  userId,
  from,
  to,
  includeTest,
  tz,
}: {
  userId: number;
  from?: string;
  to?: string;
  includeTest: boolean;
  tz: string;
}) {
  const { data, isLoading, error } = useAdminGetEmailVolumeForUser({
    userId,
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    includeTest,
    tz,
  });

  if (isLoading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="py-20 text-center text-destructive">Couldn't load detail.</div>
    );
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>{data.name ?? data.email ?? `User #${data.userId}`}</SheetTitle>
        <SheetDescription>
          {data.from === data.to ? data.from : `${data.from} – ${data.to}`} · {data.total} sent ·{" "}
          {data.queued} queued
        </SheetDescription>
      </SheetHeader>

      <div className="mt-6 space-y-6">
        <div>
          <div className="text-sm font-medium mb-2">Daily volume</div>
          <div className="h-64" data-testid="drill-daily-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="sent" stackId="a" fill="hsl(221 83% 53%)" name="Sent" />
                <Bar dataKey="queued" stackId="a" fill="hsl(38 92% 50%)" name="Queued" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <div className="text-sm font-medium mb-2">By template</div>
          <Table data-testid="drill-template-table">
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead className="text-right">Sent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.perTemplate.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-muted-foreground">
                    No sends in this window.
                  </TableCell>
                </TableRow>
              ) : (
                data.perTemplate.map((c) => (
                  <TableRow key={c.template.id ?? "none"}>
                    <TableCell>{c.template.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.count}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}
